import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

// Reçu de paiement of a charge (expense), rendered with @react-pdf/renderer
const styles = StyleSheet.create({
  page: { padding: 36, fontSize: 10, fontFamily: "Helvetica", color: "#111" },
  header: { flexDirection: "row", justifyContent: "space-between", marginBottom: 24 },
  company: { width: "55%" },
  companyName: { fontSize: 14, fontFamily: "Helvetica-Bold", marginBottom: 4 },
  muted: { color: "#555", marginBottom: 2 },
  receiptBox: { width: "40%", borderWidth: 1, borderColor: "#111", padding: 8, alignItems: "flex-end" },
  receiptTitle: { fontSize: 14, fontFamily: "Helvetica-Bold", marginBottom: 6 },
  sectionTitle: { fontSize: 11, fontFamily: "Helvetica-Bold", marginTop: 14, marginBottom: 6 },
  infoRow: { flexDirection: "row", marginBottom: 3 },
  infoLabel: { width: 90, color: "#555" },
  infoValue: { flex: 1 },
  table: { borderWidth: 1, borderColor: "#999" },
  tableHeader: { flexDirection: "row", backgroundColor: "#222", color: "#fff", fontFamily: "Helvetica-Bold" },
  tableRow: { flexDirection: "row", borderTopWidth: 1, borderTopColor: "#ddd" },
  cell: { padding: 5 },
  right: { textAlign: "right" },
  totals: { marginTop: 12, marginLeft: "auto", width: "45%" },
  totalRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 3 },
  totalStrong: { fontFamily: "Helvetica-Bold", borderTopWidth: 1, borderTopColor: "#111", paddingTop: 5 },
  status: { marginTop: 10, fontFamily: "Helvetica-Bold" },
  signature: { marginTop: 36, marginLeft: "auto", width: "45%", borderTopWidth: 1, borderTopColor: "#111", paddingTop: 4, textAlign: "center", color: "#555" },
  footer: { position: "absolute", bottom: 24, left: 36, right: 36, textAlign: "center", color: "#888", fontSize: 8 },
});

const formatAmount = (value) =>
  `${Number(value || 0).toFixed(3)} DNT`;

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("fr-FR") : "";

// cheque / virement / traite reference of a payment, when there is one
const paymentReference = (payment) =>
  payment.codeCheque || payment.numVirement || payment.reference || "";

const ChargeReceiptPdf = ({ societe, charge, payments, totalPaid, restToPay }) => {
  const fullyPaid = Number(restToPay) <= 0;
  const period = [charge.month, charge.year].filter(Boolean).join(" / ");

  return (
    <Document title={`Reçu de paiement ${charge.ref || ""}`}>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.company}>
            <Text style={styles.companyName}>{societe?.title || ""}</Text>
            {societe?.adresse ? <Text style={styles.muted}>{societe.adresse}</Text> : null}
            {societe?.matricule ? <Text style={styles.muted}>MF : {societe.matricule}</Text> : null}
            {societe?.numPhone ? <Text style={styles.muted}>Tél : {societe.numPhone}</Text> : null}
            {societe?.email ? <Text style={styles.muted}>{societe.email}</Text> : null}
          </View>
          <View style={styles.receiptBox}>
            <Text style={styles.receiptTitle}>REÇU DE PAIEMENT</Text>
            <Text>N° REC-{charge.ref || charge.id}</Text>
            <Text>Date : {formatDate(new Date())}</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Charge</Text>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Référence</Text>
          <Text style={styles.infoValue}>{charge.ref}</Text>
        </View>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Intitulé</Text>
          <Text style={styles.infoValue}>{charge.title}</Text>
        </View>
        {period ? (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Période</Text>
            <Text style={styles.infoValue}>{period}</Text>
          </View>
        ) : null}
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Date de la charge</Text>
          <Text style={styles.infoValue}>{formatDate(charge.creationDate)}</Text>
        </View>
        {charge.description ? (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Description</Text>
            <Text style={styles.infoValue}>{charge.description}</Text>
          </View>
        ) : null}

        {charge.chargeValues && charge.chargeValues.length > 0 ? (
          <>
            <Text style={styles.sectionTitle}>Détail</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={[styles.cell, { width: "70%" }]}>Libellé</Text>
                <Text style={[styles.cell, styles.right, { width: "30%" }]}>Montant</Text>
              </View>
              {charge.chargeValues.map((line, i) => (
                <View style={styles.tableRow} key={i}>
                  <Text style={[styles.cell, { width: "70%" }]}>
                    {[line.rate?.code, line.rate?.name].filter(Boolean).join(" - ")}
                  </Text>
                  <Text style={[styles.cell, styles.right, { width: "30%" }]}>
                    {formatAmount(line.price)}
                  </Text>
                </View>
              ))}
            </View>
          </>
        ) : null}

        <Text style={styles.sectionTitle}>Paiements</Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.cell, { width: "22%" }]}>Date</Text>
            <Text style={[styles.cell, { width: "28%" }]}>Mode</Text>
            <Text style={[styles.cell, { width: "25%" }]}>Référence</Text>
            <Text style={[styles.cell, styles.right, { width: "25%" }]}>Montant</Text>
          </View>
          {payments.map((payment, i) => (
            <View style={styles.tableRow} key={i}>
              <Text style={[styles.cell, { width: "22%" }]}>{formatDate(payment.creationDate)}</Text>
              <Text style={[styles.cell, { width: "28%" }]}>{payment.typePayement || ""}</Text>
              <Text style={[styles.cell, { width: "25%" }]}>{paymentReference(payment)}</Text>
              <Text style={[styles.cell, styles.right, { width: "25%" }]}>
                {formatAmount(payment.amountPayed)}
              </Text>
            </View>
          ))}
        </View>

        <View style={styles.totals}>
          <View style={styles.totalRow}>
            <Text>Total de la charge</Text>
            <Text>{formatAmount(charge.price)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text>Total payé</Text>
            <Text>{formatAmount(totalPaid)}</Text>
          </View>
          <View style={[styles.totalRow, styles.totalStrong]}>
            <Text>Reste à payer</Text>
            <Text>{formatAmount(restToPay)}</Text>
          </View>
        </View>

        <Text style={styles.status}>
          {fullyPaid ? "Charge payée intégralement." : "Paiement partiel."}
        </Text>

        <Text style={styles.signature}>Signature et cachet</Text>

        <Text style={styles.footer} fixed>
          Reçu généré le {new Date().toLocaleString("fr-FR")}
        </Text>
      </Page>
    </Document>
  );
};

export default ChargeReceiptPdf;
